import React, { useEffect, useRef, useState } from "react";
import { adminApi } from "../../lib/api";
import { toast } from "sonner";
import { Upload, CheckCircle2, AlertTriangle, RefreshCw, FileText, Trash2 } from "lucide-react";

const MAX_MB = 10;

export default function TermReportsBulkPanel() {
  const [config, setConfig] = useState({ terms: ["Term 1", "Term 2", "Term 3"], current_session: "" });
  const [session, setSession] = useState("");
  const [term, setTerm] = useState("Term 1");
  const [files, setFiles] = useState([]);
  const [result, setResult] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInput = useRef(null);

  useEffect(() => {
    adminApi.get("/admin/term-reports/config")
      .then(({ data }) => { setConfig(data); setSession(data.current_session); setTerm(data.terms[0]); })
      .catch(() => {});
  }, []);

  const pickFiles = (e) => {
    const picked = Array.from(e.target.files || []).filter((f) => (f.type === "application/pdf" || f.name.toLowerCase().endsWith(".pdf")));
    const oversize = picked.filter((f) => f.size > MAX_MB * 1024 * 1024);
    if (oversize.length > 0) toast.warning(`${oversize.length} file(s) larger than ${MAX_MB} MB will be rejected on upload.`);
    setFiles(picked);
    setResult(null);
  };

  const removeFile = (idx) => {
    setFiles((f) => f.filter((_, i) => i !== idx));
  };

  const submit = async () => {
    if (files.length === 0) return toast.error("Pick some PDFs first");
    if (!session.trim()) return toast.error("Enter a session");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("session", session.trim());
      fd.append("term", term);
      files.forEach((f) => fd.append("files", f));
      const { data } = await adminApi.post("/admin/term-reports/bulk", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 300000,
      });
      setResult(data);
      const ok = data.results.filter((r) => r.status === "uploaded" || r.status === "replaced").length;
      toast.success(`${ok} report${ok === 1 ? "" : "s"} uploaded`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk upload failed");
    } finally { setUploading(false); }
  };

  const reset = () => { setFiles([]); setResult(null); if (fileInput.current) fileInput.current.value = ""; };

  return (
    <div className="space-y-5 max-w-3xl">
      <div>
        <h2 className="font-display text-3xl text-[#0F1B47] font-medium">Term reports · Bulk upload</h2>
        <p className="text-sm text-[#718096]">Drop many PDFs at once — the system auto-matches each file to a student by admission ID in the filename (e.g. <code className="font-mono text-xs">NEY-297-26-27-003.pdf</code>).</p>
      </div>

      <div className="bg-[#FDFDF9] rounded-3xl border border-[#0F1B47]/8 p-5 shadow-sm">
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div>
            <div className="text-[10px] font-bold tracking-[0.16em] uppercase text-[#648DE5] mb-1">Session</div>
            <input value={session} onChange={(e) => setSession(e.target.value)} className="w-full text-sm bg-[#F5F5F0] rounded-xl px-3 py-2 border border-[#0F1B47]/8" placeholder="2026-27" data-testid="bulk-session" />
          </div>
          <div>
            <div className="text-[10px] font-bold tracking-[0.16em] uppercase text-[#648DE5] mb-1">Term</div>
            <select value={term} onChange={(e) => setTerm(e.target.value)} className="w-full text-sm bg-[#F5F5F0] rounded-xl px-3 py-2 border border-[#0F1B47]/8" data-testid="bulk-term">
              {config.terms.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        </div>

        <input ref={fileInput} type="file" multiple accept="application/pdf,.pdf" onChange={pickFiles} className="hidden" data-testid="bulk-file-input" />
        <button type="button" onClick={() => fileInput.current?.click()} className="w-full border-2 border-dashed border-[#0F1B47]/20 rounded-2xl px-4 py-8 text-center hover:border-[#0F1B47]/40 transition" data-testid="bulk-pick-files">
          {files.length > 0 ? (
            <div>
              <div className="font-display text-2xl text-[#0F1B47]">{files.length} PDF{files.length === 1 ? "" : "s"} selected</div>
              <div className="text-[11px] text-[#718096] mt-1">Click again to change</div>
            </div>
          ) : (
            <>
              <div className="font-display text-2xl text-[#0F1B47]">Drop many PDFs</div>
              <div className="text-[11px] text-[#718096] mt-1">Name each file with the child&apos;s admission ID so we auto-match.<br/>e.g. <code className="font-mono">NEY-297-26-27-003.pdf</code></div>
            </>
          )}
        </button>

        {files.length > 0 && !result && (
          <div className="mt-4 space-y-1 max-h-64 overflow-y-auto">
            {files.map((f, i) => (
              <div key={i} className="flex items-center gap-3 bg-[#F5F5F0] rounded-lg px-3 py-2 text-[12px]">
                <FileText size={13} className="text-[#0F1B47] flex-shrink-0" />
                <span className="font-mono flex-1 truncate">{f.name}</span>
                <span className="text-[10px] text-[#718096]">{(f.size / 1024).toFixed(0)} KB</span>
                <button onClick={() => removeFile(i)} className="text-[#C53030] hover:bg-[#FED7D7] rounded p-1"><Trash2 size={11} /></button>
              </div>
            ))}
          </div>
        )}

        {result && <BulkResults result={result} />}

        <div className="flex gap-2 mt-4">
          {result ? (
            <button onClick={reset} className="flex-1 inline-flex items-center justify-center gap-2 bg-[#F5F5F0] text-[#0F1B47] rounded-full py-3 font-bold text-sm" data-testid="bulk-reset">
              <RefreshCw size={14} /> Start over
            </button>
          ) : (
            <button onClick={submit} disabled={uploading || files.length === 0} className="flex-1 inline-flex items-center justify-center gap-2 bg-[#0F1B47] text-[#FDFDF9] rounded-full py-3 font-bold text-sm disabled:opacity-50" data-testid="bulk-upload-btn">
              <Upload size={14} /> {uploading ? "Uploading…" : `Upload ${files.length} report${files.length === 1 ? "" : "s"}`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function BulkResults({ result }) {
  const ok = result.results.filter((r) => r.status === "uploaded");
  const replaced = result.results.filter((r) => r.status === "replaced");
  const unmatched = result.results.filter((r) => r.status === "unmatched");
  const errored = result.results.filter((r) => r.status === "error");

  return (
    <div className="mt-4 space-y-3">
      <div className="flex gap-2 flex-wrap">
        {ok.length > 0 && <span className="bg-[#E6FFFA] text-[#2F855A] text-[11px] font-bold rounded-full px-3 py-1">{ok.length} uploaded</span>}
        {replaced.length > 0 && <span className="bg-[#FFF5B7] text-[#B7791F] text-[11px] font-bold rounded-full px-3 py-1">{replaced.length} replaced</span>}
        {unmatched.length > 0 && <span className="bg-[#FED7D7] text-[#C53030] text-[11px] font-bold rounded-full px-3 py-1">{unmatched.length} unmatched</span>}
        {errored.length > 0 && <span className="bg-[#FED7D7] text-[#C53030] text-[11px] font-bold rounded-full px-3 py-1">{errored.length} errored</span>}
      </div>
      <div className="space-y-1 max-h-64 overflow-y-auto" data-testid="bulk-results-list">
        {result.results.map((r, i) => (
          <div key={i} className="flex items-center gap-3 bg-[#F5F5F0] rounded-lg px-3 py-2 text-[12px]">
            {r.status === "uploaded" || r.status === "replaced" ? (
              <CheckCircle2 size={14} className="text-[#2F855A] flex-shrink-0" />
            ) : (
              <AlertTriangle size={14} className="text-[#C53030] flex-shrink-0" />
            )}
            <span className="font-mono flex-1 truncate">{r.filename}</span>
            {r.student_name && <span className="text-[11px] text-[#0F1B47] font-semibold">{r.student_name}</span>}
            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider ${
              r.status === "uploaded" ? "bg-[#E6FFFA] text-[#2F855A]" :
              r.status === "replaced" ? "bg-[#FFF5B7] text-[#B7791F]" :
              "bg-[#FED7D7] text-[#C53030]"
            }`}>{r.status}</span>
            {r.detail && <span className="text-[10px] text-[#718096]">{r.detail}</span>}
          </div>
        ))}
      </div>
    </div>
  );
}
